import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging'
import { SaveObject, ReadObject } from './storage'
import { SaveWebPushToken } from './notifications'

const WEB_PUSH_STATUS_KEY = 'web_push_status'
const WEB_PUSH_TOKEN_KEY = 'web_push_token'
const WEB_PUSH_SESSION_DISMISSED_KEY = 'web_push_session_dismissed'
const SERVICE_WORKER_PATH = '/firebase-messaging-sw.js'

let firebaseApp = null
let foregroundUnsubscribe = null

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
}

const firebaseEnvKeys = {
    apiKey: 'REACT_APP_FIREBASE_API_KEY',
    authDomain: 'REACT_APP_FIREBASE_AUTH_DOMAIN',
    projectId: 'REACT_APP_FIREBASE_PROJECT_ID',
    storageBucket: 'REACT_APP_FIREBASE_STORAGE_BUCKET',
    messagingSenderId: 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    appId: 'REACT_APP_FIREBASE_APP_ID',
}

const getMissingFirebaseEnvKeys = () =>
    Object.entries(firebaseConfig)
        .filter(([, value]) => !value)
        .map(([key]) => firebaseEnvKeys[key])

const logWebPushBlocked = (reason, details = {}) => {
    console.warn('[web-push] support blocked:', reason, details)
}

const isSecureContextForPush = () => {
    if (typeof window === 'undefined') return false
    if (window.isSecureContext) return true
    return ['localhost', '127.0.0.1'].includes(window.location.hostname)
}

const getBrowserName = () => {
    if (typeof navigator === 'undefined') return null

    const userAgent = navigator.userAgent || ''
    if (userAgent.includes('Edg/')) return 'edge'
    if (userAgent.includes('Firefox/')) return 'firefox'
    if (userAgent.includes('Chrome/')) return 'chrome'
    if (userAgent.includes('Safari/')) return 'safari'
    return 'unknown'
}

const normalizeForegroundPayload = (payload = {}) => {
    const notification = payload.notification || {}
    const data = payload.data || {}

    return {
        title: notification.title || data.title || 'Notification',
        body: notification.body || data.body || data.text || '',
        url: data.url || '/',
    }
}

export const getStoredWebPushStatus = () => ReadObject(WEB_PUSH_STATUS_KEY) || null

export const saveWebPushStatus = (status, extra = {}) => {
    SaveObject(WEB_PUSH_STATUS_KEY, {
        status,
        updatedAt: new Date().toISOString(),
        ...extra,
    })
}

export const saveStoredWebPushToken = (token) => {
    SaveObject(WEB_PUSH_TOKEN_KEY, {
        token,
        updatedAt: new Date().toISOString(),
    })
}

export const readStoredWebPushToken = () => ReadObject(WEB_PUSH_TOKEN_KEY) || null

const wasDismissedInCurrentSession = () => {
    try {
        return sessionStorage.getItem(WEB_PUSH_SESSION_DISMISSED_KEY) === 'true'
    } catch (error) {
        return false
    }
}

const dismissForCurrentSession = () => {
    try {
        sessionStorage.setItem(WEB_PUSH_SESSION_DISMISSED_KEY, 'true')
    } catch (error) { }
}

const clearSessionDismiss = () => {
    try {
        sessionStorage.removeItem(WEB_PUSH_SESSION_DISMISSED_KEY)
    } catch (error) { }
}

export const isWebPushSupported = async () => {
    if (typeof window === 'undefined') {
        logWebPushBlocked('window-unavailable')
        return false
    }

    const missingFirebaseEnvKeys = getMissingFirebaseEnvKeys()
    if (missingFirebaseEnvKeys.length) {
        logWebPushBlocked('firebase-env-ausente', { missing: missingFirebaseEnvKeys })
        return false
    }

    if (!process.env.REACT_APP_FIREBASE_VAPID_KEY) {
        logWebPushBlocked('vapid-key-ausente', { missing: ['REACT_APP_FIREBASE_VAPID_KEY'] })
        return false
    }

    if (!isSecureContextForPush()) {
        logWebPushBlocked('contexto-inseguro-sem-https', {
            protocol: window.location.protocol,
            hostname: window.location.hostname,
        })
        return false
    }

    if (typeof Notification === 'undefined') {
        logWebPushBlocked('notification-api-ausente')
        return false
    }

    if (Notification.permission === 'denied') {
        logWebPushBlocked('permissao-negada')
        return false
    }

    if (!('serviceWorker' in navigator)) {
        logWebPushBlocked('service-worker-ausente')
        return false
    }

    try {
        const supported = await isSupported()
        if (!supported) {
            logWebPushBlocked('firebase-messaging-nao-suportado')
        }
        return supported
    } catch (error) {
        logWebPushBlocked('firebase-messaging-suporte-erro', { message: error?.message })
        return false
    }
}

export const shouldPromptForWebPush = () => {
    const currentPermission = typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
    const storedStatus = getStoredWebPushStatus()
    const storedToken = readStoredWebPushToken()

    if (currentPermission === 'granted' || currentPermission === 'denied') {
        return false
    }

    if (wasDismissedInCurrentSession()) {
        return false
    }

    if (!storedStatus) {
        return true
    }

    if (storedStatus.status === 'accepted' && storedToken?.token) {
        return false
    }

    if (storedStatus.status === 'denied') {
        return false
    }

    return true
}

const getFirebaseApp = () => {
    if (!firebaseApp) {
        firebaseApp = initializeApp(firebaseConfig)
    }

    return firebaseApp
}

export const registerNotificationServiceWorker = async () => {
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH)
    await navigator.serviceWorker.ready
    return registration
}

export const syncWebPushToken = async () => {
    const supported = await isWebPushSupported()
    if (!supported) return null
    if (Notification.permission !== 'granted') return null

    const serviceWorkerRegistration = await registerNotificationServiceWorker()
    const app = getFirebaseApp()
    const messaging = getMessaging(app)
    const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration,
    })

    if (!token) {
        return null
    }

    const browser = getBrowserName()
    await SaveWebPushToken({
        token,
        provider: 'fcm',
        platform: 'web',
        browser,
    })
    saveStoredWebPushToken(token)
    saveWebPushStatus('accepted', { browser })
    clearSessionDismiss()

    return token
}

export const requestAndEnableWebPush = async () => {
    const supported = await isWebPushSupported()
    if (!supported) {
        return { ok: false, reason: 'unsupported' }
    }

    const permission = await Notification.requestPermission()

    if (permission === 'granted') {
        const token = await syncWebPushToken()
        return { ok: !!token, permission, token }
    }

    if (permission === 'denied') {
        saveWebPushStatus('denied')
    } else {
        dismissWebPushPrompt()
    }

    return { ok: false, permission }
}

export const dismissWebPushPrompt = () => {
    dismissForCurrentSession()
    saveWebPushStatus('dismissed')
}

export const markWebPushDenied = () => {
    saveWebPushStatus('denied')
}

export const subscribeToForegroundMessages = async (handler) => {
    const supported = await isWebPushSupported()
    if (!supported) return () => {}

    const app = getFirebaseApp()
    const messaging = getMessaging(app)

    if (foregroundUnsubscribe) {
        foregroundUnsubscribe()
    }

    foregroundUnsubscribe = onMessage(messaging, (payload) => {
        handler(normalizeForegroundPayload(payload), payload)
    })

    return () => {
        if (foregroundUnsubscribe) {
            foregroundUnsubscribe()
            foregroundUnsubscribe = null
        }
    }
}
